import React from 'react';
import { useTranslation } from 'react-i18next';
import { IonList, IonItem, IonLabel, IonIcon } from '@ionic/react';
import { checkmarkCircle, businessOutline } from 'ionicons/icons';
import ProPageModal from './ProPageModal';
import type { EligibleWorkspace } from '../../../pages/pro/resolveTrackingWorkspace';

export interface ProTrackingWorkspacePickerProps {
  isOpen: boolean;
  eligible: EligibleWorkspace[];
  selectedId?: string | null;
  onSelect: (id: string) => void;
  onDismiss: () => void;
}

/**
 * Pro mobile picker for the tracking destination workspace.
 *
 * Built on `ProPageModal`. Lists eligible workspaces as IonItem rows; choosing a
 * row calls `onSelect(id)` (caller closes). Backdrop / close dismisses without
 * calling `onSelect` — no write on dismiss.
 */
const ProTrackingWorkspacePicker: React.FC<ProTrackingWorkspacePickerProps> = ({
  isOpen,
  eligible,
  selectedId = null,
  onSelect,
  onDismiss,
}) => {
  const { t } = useTranslation('pro');

  return (
    <ProPageModal
      isOpen={isOpen}
      onDismiss={onDismiss}
      title={t('trackingWorkspace.pickerTitle')}
    >
      <IonList lines="inset">
        {eligible.map((ws) => {
          const selected = selectedId === ws.id;
          return (
            <IonItem
              key={ws.id}
              button
              onClick={() => onSelect(ws.id)}
              detail={false}
            >
              <IonIcon
                icon={businessOutline}
                slot="start"
                color="medium"
                className="text-xl"
              />
              <IonLabel>
                <h3 className="text-sm font-semibold">{ws.name}</h3>
                {ws.organization_name ? (
                  <p className="text-xs text-text-tertiary dark:text-text-dark-tertiary">
                    {ws.organization_name}
                  </p>
                ) : null}
              </IonLabel>
              {selected && (
                <IonIcon slot="end" icon={checkmarkCircle} color="primary" />
              )}
            </IonItem>
          );
        })}
      </IonList>
    </ProPageModal>
  );
};

export default ProTrackingWorkspacePicker;
